import { useEffect, useState } from "react";

import {
  ESPERA_VERSION,
  actualizarAhora,
  buscarVersionNueva,
  cortarRecarga,
  esperarQuietud,
  pedirActualizar,
  sePuedeComprobarVersion,
  tocaRecargarPorVersion,
} from "../../pwa";

/* LAS PANTALLAS QUE EMITEN PAPELES ENTRAN CON LA VERSIÓN AL DÍA.
 *
 * ── POR QUÉ ──────────────────────────────────────────────────────────────────
 *
 * Fabián, 09/10: emitió un MIC con la casilla 27 mal porque su pestaña del hub
 * era la de la mañana; el arreglo se había publicado a las 11:15 y él nunca
 * recargó. El resto del hub sigue con el cartel naranja y sin recargar solo
 * («SIN recargar solo ni sacar a nadie de la sesión»): ahí una versión vieja
 * es, como mucho, una pantalla que todavía no cambió. Acá es un papel que se
 * presenta en la frontera.
 *
 * Igna, 09/10: «que el Hub se actualice solo al entrar a Aduana › CRT o MIC si
 * hay versión nueva y no hay nada a medio cargar. Así no se emite un papel con
 * una versión vieja.» → «Sí, hacelo».
 *
 * ── CÓMO ─────────────────────────────────────────────────────────────────────
 *
 * Al entrar, ANTES de dibujar la pantalla, se mira si hay versión nueva
 * (`buscarVersionNueva`: compara el paquete de esta pestaña con el publicado).
 * Mientras tanto se lee «Comprobando…»: casi siempre es un instante. Mirar
 * nunca tarda más de 2,5 s, y si hay versión nueva con algo guardándose, se
 * esperan como mucho 3 s más (regla 3, abajo) — sin red, sin service worker,
 * en desarrollo o en los tests se pasa de largo enseguida.
 *
 *   - No hay nada nuevo → se dibuja la pantalla, como siempre.
 *   - Hay versión nueva, recién vista en el servidor, y nada a medio cargar →
 *     «Actualizando el Hub a la versión nueva…» y se recarga EN LA MISMA
 *     DIRECCIÓN, con su `?ver=` si venía del buscador: la pantalla todavía no
 *     lo leyó, así que después de recargar abre el mismo papel.
 *   - Hay versión nueva pero no se puede recargar → se dibuja la pantalla y el
 *     cartel queda abierto pidiendo actualizar antes de emitir. Guardar no se
 *     frena nunca; si igual se guarda con la vieja, el cartel lo vuelve a decir
 *     (`avisarTrasGuardar`, en CRT y MIC).
 *
 * ── «NADA A MEDIO CARGAR», LA REGLA ──────────────────────────────────────────
 *
 * La más simple que no pierde nada:
 *
 *   1. Sólo se recarga ANTES de dibujar la pantalla. El formulario del CRT o
 *      del MIC todavía no existe, así que no puede tener nada tipeado; y los
 *      borradores viven en la base, no en el navegador. Una vez dibujada, la
 *      pantalla NO se recarga sola nunca: si la versión nueva aparece después,
 *      es cartel. Por eso, si la recarga sale y no llega, se la CORTA antes de
 *      dibujar (`cortarRecarga`): una recarga que llega tarde borraría lo que
 *      se haya empezado a tipear. Lo mismo si la persona se va a otra pantalla
 *      mientras dice «Actualizando…».
 *   2. Sólo con la versión RECIÉN VISTA en el servidor (`fresca`). Lo que se
 *      sabía de antes sirve para el cartel; si ahora el servidor no contesta,
 *      la recarga tampoco llegaría.
 *   3. La pantalla de antes ya se desmontó al navegar, pero puede haber dejado
 *      algo: trabajo sin guardar que frena el cierre de la pestaña
 *      (`beforeunload`), o un guardado viajando a la base —Mi espacio manda el
 *      libro entero al salir, una foto se sigue subiendo—. Se espera un rato
 *      corto a que todo quede quieto (`esperarQuietud`, 3 s como mucho); si no
 *      se aquieta, no se recarga: cartel.
 *   4. Una sola recarga por versión y por minuto (`tocaRecargarPorVersion`):
 *      si después de recargar sigue pendiente, queda el cartel. Ni un bucle.
 */

/* Cuánto se espera a que llegue la recarga antes de cortarla y seguir con la
 * versión que hay. El `index.html` acaba de contestar en menos de 2,5 s, así
 * que normalmente llega enseguida. */
const TOPE_ACTUALIZANDO = 8000;

export default function AlDiaParaEmitir({ children }) {
  const [paso, setPaso] = useState(() =>
    sePuedeComprobarVersion() ? "comprobando" : "listo",
  );

  useEffect(() => {
    if (!sePuedeComprobarVersion()) return undefined;
    let vivo = true;
    let recargando = false;
    let relojes = [];
    const seguir = () => {
      if (vivo) setPaso("listo");
    };
    const conCartel = () => {
      pedirActualizar("antes-de-emitir");
      seguir();
    };

    /* Un segundo tope, por las dudas: si la consulta se colgara por un error
       nuestro, la pantalla que emite no puede quedar tapada para siempre. */
    const tope = new Promise((ok) => {
      relojes.push(setTimeout(
        () => ok({ nueva: false, objetivo: null, fresca: false }),
        ESPERA_VERSION + 500,
      ));
    });

    (async () => {
      const { nueva, objetivo, fresca } = await Promise.race([buscarVersionNueva(), tope]);
      if (!vivo) return;
      if (!nueva) { seguir(); return; }
      if (!fresca) { conCartel(); return; }
      const quieto = await esperarQuietud({ vigente: () => vivo });
      if (!vivo) return;
      if (!quieto || !tocaRecargarPorVersion(objetivo)) { conCartel(); return; }
      setPaso("actualizando");
      recargando = true;
      actualizarAhora();
      /* Si la recarga no llega (red mala, o alguien la frenó en el diálogo
         del navegador), se la corta y recién ahí se dibuja la pantalla, con
         el cartel. */
      relojes.push(setTimeout(() => {
        recargando = false;
        cortarRecarga();
        conCartel();
      }, TOPE_ACTUALIZANDO));
    })().catch(seguir);

    return () => {
      vivo = false;
      relojes.forEach(clearTimeout);
      relojes = [];
      /* Se fue a otra pantalla con la recarga en camino: se corta, para que no
         caiga encima de lo que haga allá. */
      if (recargando) cortarRecarga();
    };
  }, []);

  if (paso === "comprobando") {
    return <p className="tenue">Comprobando que el Hub esté al día…</p>;
  }
  if (paso === "actualizando") {
    return <p className="tenue">Actualizando el Hub a la versión nueva…</p>;
  }
  return children;
}
